// Explicit React import so consumers can render this block under a classic-JSX
// test runner (AlwaysAI's `tsx --test` lane); the ui/ primitives already do.
import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { Inbox } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

type EmptyStateProps = {
  icon?: LucideIcon;
  title: string;
  description: string;
  actions?: ReactNode;
  tone?: "neutral" | "info" | "danger";
};

const toneStyles = {
  neutral: { container: "border-border bg-card", icon: "text-muted-foreground" },
  info: { container: "border-info-soft bg-info-soft/50", icon: "text-info" },
  danger: { container: "border-danger-soft bg-danger-soft/50", icon: "text-danger" },
} satisfies Record<
  NonNullable<EmptyStateProps["tone"]>,
  { container: string; icon: string }
>;

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  actions,
  tone = "neutral",
}: EmptyStateProps) {
  const styles = toneStyles[tone];

  return (
    <div
      className={cn(
        "flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed px-6 py-12 text-center",
        styles.container
      )}
    >
      <div className={cn("flex items-center justify-center", styles.icon)}>
        <Icon className="size-9" strokeWidth={1.25} />
      </div>
      <h3 className="mt-4 font-sans text-base font-semibold text-foreground">
        {title}
      </h3>
      <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
        {description}
      </p>
      {actions ? (
        <div className="mt-5 flex flex-wrap justify-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}
